import type { AdminDirectoryOption, AdminUser, AdminUserSupervisorEmbed, OrganizationKey, StaffUserFormState } from '@/types/user-admin';
import { ADMIN_ROLE_KEY, ORGANIZATION_KEYS, STAFF_ROLE_KEY } from '@/utils/role';

export function resolveAdminUserOrganizationKey(user: Pick<AdminUser, 'organizationKey' | 'isInternal' | 'user_type'>): OrganizationKey {
    if (user.organizationKey === ORGANIZATION_KEYS.SILVERSTAY || user.organizationKey === ORGANIZATION_KEYS.HOSPITAL) {
        return user.organizationKey;
    }
    const isInternal = typeof user.isInternal === 'boolean' ? user.isInternal : (user.user_type ?? '').toLowerCase() === 'internal';
    return isInternal ? ORGANIZATION_KEYS.SILVERSTAY : ORGANIZATION_KEYS.HOSPITAL;
}

const normalizeRoleLookupToken = (value?: string): string => (value ?? '').trim().replaceAll(' ', '_').toUpperCase();

/** Resolve role directory metadata for chips — prefers `role_id` when listing users from the API. */
const isAdminRoleOption = (roleId: string, roleOptions: AdminDirectoryOption[]): boolean => {
    const selected = roleOptions.find((option) => option.id === roleId);
    if (selected) {
        const tokens = [selected.key, selected.value, selected.id].map((token) => normalizeRoleLookupToken(token));
        return tokens.includes(ADMIN_ROLE_KEY);
    }
    return normalizeRoleLookupToken(roleId) === ADMIN_ROLE_KEY;
};

/** SilverStay admins are not scoped to hospitals — hide hospital picker and send `hospitalIds: []`. */
export function isSilverStayAdminUser(organizationKey: OrganizationKey, roleId: string, roleOptions: AdminDirectoryOption[] = []): boolean {
    return organizationKey === ORGANIZATION_KEYS.SILVERSTAY && Boolean(roleId.trim()) && isAdminRoleOption(roleId, roleOptions);
}

export function getDefaultStaffRoleId(roleOptions: AdminDirectoryOption[] = []): string {
    const staffOption = roleOptions.find((option) => {
        const optionTokens = [option.key, option.value, option.id].map((token) => normalizeRoleLookupToken(token));
        return optionTokens.includes(STAFF_ROLE_KEY);
    });
    return staffOption?.id ?? STAFF_ROLE_KEY;
}

/** Hospital has no Admin role — map Admin to Staff. Staff and Supervisor stay unchanged. */
export function resolveRoleForOrganizationChange(
    organizationKey: OrganizationKey,
    currentRole: string,
    roleOptions: AdminDirectoryOption[] = []
): string {
    if (organizationKey === ORGANIZATION_KEYS.HOSPITAL && isAdminRoleOption(currentRole, roleOptions)) {
        return getDefaultStaffRoleId(roleOptions);
    }
    return currentRole;
}

export function resolveAdminStaffRoleDirectoryEntry(
    roleDisplay: string | null | undefined,
    roleOptions: AdminDirectoryOption[],
    roleId?: string | null
): AdminDirectoryOption | undefined {
    const trimmedId = roleId?.trim();
    if (trimmedId) {
        const matchById = roleOptions.find((option) => option.id === trimmedId);
        if (matchById) {
            return matchById;
        }
    }

    const rawDisplay = roleDisplay?.trim();
    if (!rawDisplay) {
        return undefined;
    }

    const normalizedDisplay = normalizeRoleLookupToken(rawDisplay);

    return roleOptions.find(
        (option) =>
            option.id === rawDisplay ||
            normalizeRoleLookupToken(option.key) === normalizedDisplay ||
            normalizeRoleLookupToken(option.value) === normalizedDisplay
    );
}

export const initialStaffUserFormState = (): StaffUserFormState => ({
    organizationKey: ORGANIZATION_KEYS.HOSPITAL,
    role: '',
    first_name: '',
    last_name: '',
    nick_name: '',
    email: '',
    title: '',
    department: '',
    mobile_number: '',
    office_number: '',
    schedule_type: '',
    supervisor_user_id: ''
});

/** Resolve canonical role directory id from staff API fields. */
export function resolveAdminUserRoleId(user: Pick<AdminUser, 'role_id' | 'role'>, roleOptions: AdminDirectoryOption[] = []): string {
    const roleId = user.role_id?.trim();
    if (roleId) {
        return roleId;
    }
    const fromDisplay = resolveAdminStaffRoleDirectoryEntry(user.role, roleOptions)?.id;
    if (fromDisplay) {
        return fromDisplay;
    }
    return user.role?.trim() ?? '';
}

export function getAdminUserDisplayName(user: AdminUser): string {
    const combined = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
    if (combined) {
        return combined;
    }
    return user.name ?? '';
}

/** Minimal `AdminUser` for supervisor Autocomplete options from embedded API supervisor. */
export function supervisorEmbedToAdminUser(embed: AdminUserSupervisorEmbed): AdminUser {
    const display = [embed.first_name, embed.last_name].filter(Boolean).join(' ').trim() || embed.name?.trim() || '';
    return {
        id: embed.id,
        email: embed.email ?? '',
        name: display,
        first_name: embed.first_name,
        last_name: embed.last_name,
        isInternal: false,
        user_type: ORGANIZATION_KEYS.HOSPITAL.toLowerCase(),
        role: '',
        hospital_id: '',
        supervisor_user_id: null
    };
}

export function getAdminUserHospitalIds(user: Pick<AdminUser, 'hospitals' | 'hospital_ids' | 'hospital_id'>): string[] {
    const fromHospitals = (user.hospitals ?? []).map((hospital) => hospital.id).filter((id): id is string => Boolean(id));
    if (fromHospitals.length > 0) {
        return fromHospitals;
    }
    if (Array.isArray(user.hospital_ids) && user.hospital_ids.length > 0) {
        return user.hospital_ids;
    }
    if (user.hospital_id) {
        return [user.hospital_id];
    }
    return [];
}
